import React, { useState } from 'react';

/**
 * Founding Member hex badge (pointy-top hexagon) — just the number inside.
 *
 * Sourced from the existing `db.users.founding_member_number` field, surfaced
 * onto the talent card via the `/api/assessments/my-assessment` and
 * `/api/assessments/talent-card/:id` endpoints under `talent_card.header.founding_member_number`.
 *
 * Props:
 *   number — integer founding-member position (e.g. 42)
 */
const FoundingBadge = ({ number }) => {
    const [hover, setHover] = useState(false);

    if (number == null) return null;

    const SIZE = 36;
    // Pointy-top hex vertices for a 24-wide x 28-tall path, centred in the 36px box
    // (top vertex, upper-right, lower-right, bottom vertex, lower-left, upper-left)
    const hexPath = 'M18 4 L31 11 L31 25 L18 32 L5 25 L5 11 Z';

    return (
        <span
            data-testid={`founding-badge-${number}`}
            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            <svg
                width={SIZE}
                height={SIZE}
                viewBox="0 0 36 36"
                xmlns="http://www.w3.org/2000/svg"
                aria-label={`Founding 1,000 — Candidate #${number}`}
                role="img"
            >
                {/* Dark purple fill + solid white number = guaranteed high-contrast,
                    legible pairing regardless of what's behind the badge. */}
                <path d={hexPath} fill="#4C1D95" stroke="#C4B5FD" strokeWidth="1.5" strokeLinejoin="round" />
                <text
                    x="18"
                    y="22"
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="700"
                    fontFamily="Barlow, sans-serif"
                    fill="#FFFFFF"
                >
                    {`#${number}`}
                </text>
            </svg>

            {hover && (
                <span
                    role="tooltip"
                    data-testid="founding-badge-tooltip"
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 6px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#0A0A0A',
                        color: '#FFFFFF',
                        fontSize: '11px',
                        fontWeight: 500,
                        fontFamily: 'Barlow, sans-serif',
                        padding: '5px 10px',
                        borderRadius: '6px',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        zIndex: 30,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.18)'
                    }}
                >
                    {`Founding 1,000 — Candidate #${number}`}
                </span>
            )}
        </span>
    );
};

export default FoundingBadge;
